import { BLOCK_TYPES, BLOCK_TYPES_V1, COLOUR_LOOKS, PARTICLE_KINDS, SHOW_WAYS, SURFACE_EFFECTS, SWARM_KINDS, WORLD_PACKAGE_LIMITS, } from './format.js';
import { lampsProblems, isPoint3, placementProblems } from './shapes.js';
import { COLOUR, ID, MESH, dutch, isNumber, isRecord, problem, show } from './util.js';
const E = WORLD_PACKAGE_LIMITS.extent;
export const BLOCK_SETTINGS = {
    water: {
        level: {
            kind: 'number',
            min: -50,
            max: 50,
            about: 'waterhoogte in meter (kan per toestand veranderen)',
        },
        radius: {
            kind: 'number',
            min: 10,
            max: 2000,
            about: 'straal van de waterschijf rond het midden, meter',
        },
        deep: { kind: 'colour', about: 'kleur van diep water' },
        shallow: { kind: 'colour', about: 'kleur van ondiep water' },
        waves: {
            kind: 'number',
            min: 0,
            max: 2,
            about: 'golfhoogte naast die van de zee (1); 0,1 voor een sloot',
        },
        foam: { kind: 'boolean', about: 'schuim waar het ondiep is' },
        surf: {
            kind: 'shore',
            about: 'branding langs een kustlijn {shore: [[x, z], …], closed}, of null',
        },
        mesh: {
            kind: 'name',
            nullable: true,
            about: "de vorm van het water: de meshes in de GLB's waarvan de naam zo begint, of null voor de schijf rond het midden (formaat 2)",
        },
    },
    grass: {
        area: {
            kind: 'points',
            min: 3,
            max: 32,
            about: 'gebied (veelhoek [[x, z], …]) waarin de pollen staan',
        },
        clumps: { kind: 'number', min: 1, max: 4000, integer: true, about: 'aantal pollen' },
        perClump: {
            kind: 'range',
            min: 1,
            max: 20,
            integer: true,
            about: 'sprieten per pol [min, max]',
        },
        spread: { kind: 'number', min: 0, max: 10, about: 'breedte van een pol, meter' },
        height: { kind: 'range', min: 0.05, max: 3, about: 'hoogte van een spriet [min, max], meter' },
        width: { kind: 'range', min: 0.05, max: 3, about: 'breedte van een spriet [min, max], meter' },
        shade: { kind: 'range', min: 0.2, max: 3, about: 'helderheid [min, max]; 1 zoals getekend' },
        above: {
            kind: 'number',
            min: -50,
            max: 200,
            about: 'groeit alleen waar de grond minstens zo hoog is, meter',
        },
        root: { kind: 'colour', about: 'kleur onderaan' },
        tip: { kind: 'colour', about: 'kleur van de punt' },
        seed: {
            kind: 'number',
            min: 0,
            max: 2_147_483_647,
            integer: true,
            about: 'getal voor het toeval (zelfde getal, zelfde pollen)',
        },
        sway: { kind: 'number', min: 0, max: 0.5, about: 'hoe ver de punt wiegt in de wind, meter' },
    },
    birds: {
        flights: {
            kind: 'flights',
            about: 'vluchten [{centre: [x, z], radius, height, speed, phase}], 1–12',
        },
        size: {
            kind: 'number',
            min: 0.2,
            max: 4,
            about: 'grootte; 1,5 is anderhalf keer een echte meeuw',
        },
        colour: { kind: 'colour', about: 'kleur' },
        emissive: { kind: 'colour', about: 'eigen licht (de onderkant, tegen de lucht)' },
    },
    surface: {
        mesh: { kind: 'name', about: "de meshes in de GLB's waarvan de naam zo begint" },
        texture: {
            kind: 'material',
            about: '{platform: "<textuur>"} of {file: "<naam>.jpg"}; moet er staan, maar bij een effect doet hij niets',
        },
        maps: {
            kind: 'maps',
            about: 'welke kaarten: diff, nor_gl, arm (een eigen textuur: alleen diff) (niet bij een effect)',
        },
        tint: {
            kind: 'colour',
            about: 'kleur waarmee de textuur vermenigvuldigd wordt (niet bij een effect)',
        },
        roughness: { kind: 'number', min: 0, max: 1, about: 'ruwheid (niet bij een effect)' },
        overlay: {
            kind: 'overlay',
            about: 'gras eroverheen {platform, tile, tint} volgens de _COVER van de mesh, of null (niet bij een effect)',
        },
        vertexColours: {
            kind: 'boolean',
            about: 'de kleuren van de GLB (COLOR_0) gebruiken (niet bij een effect)',
        },
        polygonOffset: {
            kind: 'boolean',
            about: 'iets naar achteren getekend (land onder een podiumvlak) (niet bij een effect)',
        },
        sway: {
            kind: 'number',
            min: 0,
            max: 0.5,
            about: 'hoe ver de bovenkant wiegt in de wind, meter; 0 staat stil (formaat 2; niet bij een effect). Het wiegen groeit met het kwadraat van de hoogte, van het laagste punt van de meshes van het blok tot hun top: halverwege is het een kwart. Kruinen van 10 – 15 m hoog vragen 0,5, graspollen op vlakke grond 0,12; staan pollen op een helling, verdeel ze dan over meshes per paar meter hoogte met elk een eigen blok. wereld check waarschuwt voor gebladerte dat niet of nauwelijks wiegt',
        },
        cutout: {
            kind: 'boolean',
            about: 'uitgesneden blaadjes (de alpha van de textuur), van twee kanten (formaat 2; niet bij een effect)',
        },
        effect: {
            kind: 'choice',
            options: SURFACE_EFFECTS,
            nullable: true,
            about: 'lava, caustics (lichtspel op de bodem) of wateroppervlak (van onderen gezien), of null (formaat 2). Met een effect tekent de headset de mesh met zijn eigen materiaal: texture, maps, tint, roughness, overlay, vertexColours, polygonOffset, sway en cutout doen dan niets',
        },
    },
    deeltjes: {
        kind: {
            kind: 'choice',
            options: PARTICLE_KINDS,
            about: 'zweven (stof, as, pluis, bellen, blad), rook (een pluim), aswolk (een uitbarsting), vonken (lava die opspat), vlam (een kleine vlam), stoom',
        },
        at: {
            kind: 'point3',
            about: 'waar ze vandaan komen [x, y, z]; bij zweven het midden van de bodem van hun ruimte',
        },
        area: {
            kind: 'number',
            min: 0,
            max: 200,
            about: 'zweven: straal van hun ruimte; rook, stoom: hoe ver ze opzij dwalen, meter; de gloed bij de bron is zo breed (bij 0: de eindgrootte); aswolk, vlam: niet gebruikt',
        },
        height: {
            kind: 'range',
            min: -50,
            max: 500,
            about: 'zweven: van hoe hoog tot hoe hoog boven at; rook, stoom: hoe hoog ze komen bij niveau 0 en 1; aswolk: [onderkant, top]; vonken, vlam: niet gebruikt',
        },
        count: {
            kind: 'number',
            min: 1,
            max: 800,
            integer: true,
            about: 'hoeveel (de plafonds staan in REGELS.md)',
        },
        size: {
            kind: 'range',
            min: 0.001,
            max: 200,
            about: 'grootte bij het begin en aan het eind, meter; zweven: alleen het eerste getal; vlam: [straal, hoogte]; aswolk (vaste grootte) en vonken: niet gebruikt',
        },
        colour: {
            kind: 'colour',
            about: 'kleur; bij zweven en rook kan hij per toestand veranderen; vonken: niet gebruikt',
        },
        opacity: {
            kind: 'number',
            min: 0,
            max: 1,
            about: 'dekking; bij zweven en rook kan hij per toestand veranderen; vonken: niet gebruikt',
        },
        wind: {
            kind: 'vector',
            min: -5,
            max: 5,
            about: 'alleen zweven: hoe de lucht ze meeneemt [x, y, z], meter per seconde',
        },
        rise: {
            kind: 'number',
            min: -5,
            max: 5,
            about: 'alleen zweven: hoe snel ze stijgen (negatief: dalen), meter per seconde',
        },
        seconds: {
            kind: 'number',
            min: 0.5,
            max: 120,
            about: 'rook, aswolk, stoom: hoe lang een wolkje leeft; zweven, vonken, vlam: niet gebruikt',
        },
        additive: {
            kind: 'boolean',
            about: 'alleen zweven: licht dat erbij komt (stof in een zonnestraal)',
        },
        glow: {
            kind: 'number',
            min: 0,
            max: 4,
            about: 'gloed bij de bron, 0 voor geen; bij zweven en rook kan hij per toestand veranderen, maar alleen als hij hier boven 0 staat; een aswolk heeft altijd twee gloeden onder zich',
        },
        glowColour: { kind: 'colour', about: 'kleur van de gloed' },
        level: {
            kind: 'number',
            min: 0,
            max: 1,
            about: 'hoeveel ervan is, 0–1 (kan per toestand veranderen, bij elke soort)',
        },
        seed: {
            kind: 'number',
            min: 0,
            max: 2_147_483_647,
            integer: true,
            about: 'getal voor het toeval (deeltjes: alleen zweven, aswolk en vonken)',
        },
    },
    zwerm: {
        kind: { kind: 'choice', options: SWARM_KINDS, about: 'vlinders of vissen' },
        centre: { kind: 'point3', about: 'het midden (vlinders: op de grond) [x, y, z]' },
        radius: { kind: 'number', min: 0.5, max: 100, about: 'hoe ver ze van het midden gaan, meter' },
        height: {
            kind: 'range',
            min: -50,
            max: 100,
            about: 'van hoe hoog tot hoe hoog boven het midden',
        },
        spread: {
            kind: 'number',
            min: 0,
            max: 20,
            about: 'vissen: hoe ver ze uit elkaar zwemmen, meter',
        },
        count: { kind: 'number', min: 1, max: 200, integer: true, about: 'hoeveel' },
        speed: { kind: 'number', min: 0.1, max: 5, about: 'hoe snel; 1 zoals de code-werelden' },
        size: { kind: 'number', min: 0.2, max: 4, about: 'grootte; 1 is echt' },
        colours: {
            kind: 'colours',
            min: 1,
            max: 8,
            about: 'vlinders: elk een van deze; vissen: [donkerst, lichtst]',
        },
        seed: {
            kind: 'number',
            min: 0,
            max: 2_147_483_647,
            integer: true,
            about: 'getal voor het toeval',
        },
        level: {
            kind: 'number',
            min: 0,
            max: 1,
            about: 'hoeveel er zijn, 0–1 (kan per toestand veranderen); bij 0 weg',
        },
    },
    draaien: {
        part: { kind: 'name', about: "de meshes in de GLB's waarvan de naam zo begint" },
        axis: { kind: 'vector', min: -1, max: 1, about: 'de as [x, y, z] (wordt lengte 1 gemaakt)' },
        pivot: { kind: 'point3', about: 'een punt op de as' },
        speed: {
            kind: 'number',
            min: -120,
            max: 120,
            about: 'toeren per minuut (kan per toestand veranderen)',
        },
        lag: {
            kind: 'number',
            min: 0,
            max: 30,
            about: 'seconden om op snelheid te komen of stil te vallen',
        },
    },
    zichtbaar: {
        parts: {
            kind: 'names',
            max: 16,
            about: "de meshes in de GLB's waarvan de naam zo begint. Met way vervagen delen meshes met één materiaal ook één dekking, net als bij kleur (wereld check waarschuwt als twee blokken ze verschillend verven)",
        },
        visible: { kind: 'boolean', about: 'te zien (kan per toestand veranderen)' },
        way: {
            kind: 'choice',
            options: SHOW_WAYS,
            about: 'meteen, groeien (van onderen op) of vervagen',
        },
    },
    kleur: {
        parts: {
            kind: 'names',
            max: 16,
            about: "de meshes in de GLB's waarvan de naam zo begint. Meshes die één materiaal delen krijgen samen één verf: raken twee kleur-blokken (of zichtbaar met vervagen) verschillende meshes met hetzelfde materiaal, dan wint op de headset het laatste; geef die meshes elk een eigen materiaal (wereld check waarschuwt)",
        },
        colour: { kind: 'colour', about: 'kleur (kan per toestand veranderen)' },
        emissive: {
            kind: 'colour',
            about: 'kleur van het eigen licht (kan per toestand veranderen; niet bij look licht)',
        },
        glow: {
            kind: 'number',
            min: 0,
            max: 8,
            about: 'sterkte van het eigen licht (kan per toestand veranderen; niet bij look licht)',
        },
        opacity: { kind: 'number', min: 0, max: 1, about: 'dekking (kan per toestand veranderen)' },
        look: {
            kind: 'choice',
            options: COLOUR_LOOKS,
            about: 'normaal, of licht: opgeteld en zonder schaduw (zonnestralen, een vlam)',
        },
    },
    route: {
        part: {
            kind: 'name',
            about: 'de mesh die de route aflegt (een auto); hij wordt verborgen en gekopieerd',
        },
        path: { kind: 'points', min: 2, max: 64, about: 'de route [[x, z], …]' },
        closed: { kind: 'boolean', about: 'een rondje (true) of heen naar het eind (false)' },
        y: { kind: 'number', min: -50, max: 200, about: 'hoogte van de route, meter' },
        count: {
            kind: 'number',
            min: 1,
            max: 120,
            integer: true,
            about: 'hoeveel er zijn bij niveau 1',
        },
        present: {
            kind: 'range',
            min: 0,
            max: 120,
            integer: true,
            about: '[minst, meest]: hoeveel er rijden bij niveau 0 en 1',
        },
        speed: {
            kind: 'range',
            min: 0,
            max: 60,
            about: 'snelheid [laagste, hoogste], meter per seconde',
        },
        spacing: {
            kind: 'number',
            min: 0,
            max: 500,
            about: 'afstand tussen twee, meter; 0 verdeelt ze gelijk',
        },
        fade: {
            kind: 'number',
            min: 0,
            max: 100,
            about: 'meter aan de uiteinden van een open route waar ze groeien en krimpen',
        },
        seed: {
            kind: 'number',
            min: 0,
            max: 2_147_483_647,
            integer: true,
            about: 'getal voor het toeval',
        },
        level: {
            kind: 'number',
            min: 0,
            max: 1,
            about: 'hoe druk, 0–1 (kan per toestand veranderen)',
        },
    },
    licht: {
        lamps: {
            kind: 'lamps',
            about: 'lampen [{at, colour, intensity, range, aim, angle, halo, flicker}], hooguit 24; hooguit 2 die echt licht geven',
        },
        level: {
            kind: 'number',
            min: 0,
            max: 2,
            about: 'hoe fel de lampen zijn (kan per toestand veranderen)',
        },
        sun: {
            kind: 'number',
            min: 0,
            max: 2,
            about: 'deel van het zonlicht van de wereld (kan per toestand veranderen)',
        },
        sky: {
            kind: 'number',
            min: 0,
            max: 2,
            about: 'deel van het licht van de lucht (kan per toestand veranderen)',
        },
    },
    plaatsruimte: {
        rooms: {
            kind: 'rooms',
            about: 'kamers [{id, name, area, view: {pos, look}}], 1–6: de knoppen op de lessenaar',
        },
        begin: { kind: 'id', about: 'de kamer waar de leerlingen beginnen' },
        floors: {
            kind: 'floors',
            about: 'vloeren [{area, maxSize}] waar een werkstuk mag staan, 1–16',
        },
        tables: { kind: 'tables', about: 'tafels [{area, height, maxSize}], 0–16' },
        walls: {
            kind: 'walls',
            about: 'wanden [{from, to, low, high, maxSize}] waar een werkstuk aan mag hangen, 0–32',
        },
        start: {
            kind: 'start',
            about: 'waar de werkstukken beginnen {areas: [{x, z}], from, turn}',
        },
        photos: { kind: 'photos', about: "foto's van het resultaat [{eye, look, fov}], 0–4" },
        maxSize: {
            kind: 'number',
            min: 0.1,
            max: 4,
            about: 'de grootste maat van een werkstuk, meter',
        },
        fittings: {
            kind: 'name',
            nullable: true,
            about: 'meshes die op de vloer mogen staan (vaste kasten), of null',
        },
        ceiling: { kind: 'number', min: 2, max: 20, about: 'het plafond, meter' },
    },
};
/** What a feature's state may change, per block type (format v2: numbers, colours and true/false). */
export const STATEFUL = {
    water: ['level'],
    deeltjes: ['level', 'colour', 'opacity', 'glow'], // narrowed per kind by STATEFUL_PARTICLES
    zwerm: ['level'],
    draaien: ['speed'],
    zichtbaar: ['visible'],
    kleur: ['colour', 'emissive', 'glow', 'opacity'],
    route: ['level'],
    licht: ['level', 'sun', 'sky'],
};
/**
 * What a particles block's state may change, by kind: the headset applies colour, opacity and glow
 * only to `zweven` and `rook`; the other kinds follow `level` alone.
 */
export const STATEFUL_PARTICLES = {
    zweven: ['level', 'colour', 'opacity', 'glow'],
    rook: ['level', 'colour', 'opacity', 'glow'],
};
const V2_ONLY_SETTINGS = {
    surface: ['sway', 'cutout', 'effect'],
    water: ['mesh'],
};
/** The settings a format version knows for a block type; undefined for a type it does not know. */
export function settingsFor(type, version) {
    if (version === 1 && !BLOCK_TYPES_V1.includes(type))
        return undefined;
    const all = BLOCK_SETTINGS[type];
    if (version === 2)
        return all;
    const later = V2_ONLY_SETTINGS[type] ?? [];
    return Object.fromEntries(Object.entries(all).filter(([name]) => !later.includes(name)));
}
export const BLOCK_ID = ID;
const MAPS = ['diff', 'nor_gl', 'arm'];
function isPoint(value) {
    return (Array.isArray(value) &&
        value.length === 2 &&
        isNumber(value[0]) &&
        isNumber(value[1]) &&
        Math.abs(value[0]) <= E &&
        Math.abs(value[1]) <= E);
}
function numberProblems(spec, value, path) {
    if (!isNumber(value))
        return [problem(path, 'moet een getal zijn')];
    if (spec.integer && !Number.isInteger(value))
        return [problem(path, 'moet een geheel getal zijn')];
    if (value < spec.min || value > spec.max) {
        return [
            problem(path, `moet tussen ${dutch(spec.min)} en ${dutch(spec.max)} liggen, niet ${dutch(value)}`),
        ];
    }
    return [];
}
/** Whether `value` fits `spec`; problems at `path`, in Dutch. */
export function checkValue(spec, value, path) {
    switch (spec.kind) {
        case 'number':
            return numberProblems(spec, value, path);
        case 'boolean':
            return typeof value === 'boolean' ? [] : [problem(path, 'moet true of false zijn')];
        case 'colour':
            return typeof value === 'string' && COLOUR.test(value)
                ? []
                : [problem(path, 'moet een kleur zijn als "#a1b2c3"')];
        case 'name':
            if (spec.nullable && value === null)
                return [];
            return typeof value === 'string' && MESH.test(value)
                ? []
                : [problem(path, 'moet een meshnaam zijn (kleine letters, cijfers, _ en -; hooguit 40)')];
        case 'range': {
            const ok = Array.isArray(value) &&
                value.length === 2 &&
                value.every((v) => isNumber(v) && v >= spec.min && v <= spec.max && (!spec.integer || Number.isInteger(v))) &&
                value[0] <= value[1];
            return ok
                ? []
                : [
                    problem(path, `moet [laagste, hoogste] zijn, beide tussen ${dutch(spec.min)} en ${dutch(spec.max)}`),
                ];
        }
        case 'points': {
            const ok = Array.isArray(value) &&
                value.length >= spec.min &&
                value.length <= spec.max &&
                value.every(isPoint);
            return ok ? [] : [problem(path, `moet ${spec.min} tot ${spec.max} punten [x, z] hebben`)];
        }
        case 'material': {
            const ok = isRecord(value) &&
                Object.keys(value).length === 1 &&
                ((typeof value['platform'] === 'string' && value['platform'] !== '') ||
                    (typeof value['file'] === 'string' && value['file'] !== ''));
            return ok ? [] : [problem(path, 'moet {platform: "<naam>"} of {file: "<bestand>"} zijn')];
        }
        case 'maps': {
            const ok = Array.isArray(value) &&
                value.length >= 1 &&
                new Set(value).size === value.length &&
                value.every((m) => MAPS.includes(m));
            return ok
                ? []
                : [problem(path, 'moet een lijst zijn uit diff, nor_gl en arm, elk hooguit één keer')];
        }
        case 'shore': {
            if (value === null)
                return [];
            if (!isRecord(value) ||
                typeof value['closed'] !== 'boolean' ||
                Object.keys(value).length !== 2) {
                return [problem(path, 'moet null zijn of {shore: [[x, z], …], closed: true|false}')];
            }
            return checkValue({ kind: 'points', min: 2, max: 400, about: '' }, value['shore'], `${path}/shore`);
        }
        case 'flights': {
            if (!Array.isArray(value) || value.length < 1 || value.length > 12) {
                return [problem(path, 'moet 1 tot 12 vluchten hebben')];
            }
            return value.flatMap((flight, i) => {
                const at = `${path}/${i}`;
                if (!isRecord(flight))
                    return [problem(at, 'moet {centre, radius, height, speed, phase} zijn')];
                const known = ['centre', 'radius', 'height', 'speed', 'phase'];
                return [
                    ...Object.keys(flight)
                        .filter((k) => !known.includes(k))
                        .map((k) => problem(`${at}/${k}`, 'onbekende instelling voor een vlucht')),
                    ...(isPoint(flight['centre'])
                        ? []
                        : [problem(`${at}/centre`, 'moet een punt [x, z] zijn')]),
                    ...numberProblems({ min: 2, max: 300 }, flight['radius'], `${at}/radius`),
                    ...numberProblems({ min: 3, max: 300 }, flight['height'], `${at}/height`),
                    ...numberProblems({ min: -30, max: 30 }, flight['speed'], `${at}/speed`),
                    ...numberProblems({ min: -10, max: 10 }, flight['phase'], `${at}/phase`),
                ];
            });
        }
        case 'choice':
            if (spec.nullable && value === null)
                return [];
            return typeof value === 'string' && spec.options.includes(value)
                ? []
                : [
                    problem(path, `moet een van ${spec.options.join(', ')}${spec.nullable ? ' of null' : ''} zijn`),
                ];
        case 'point3':
            return isPoint3(value) ? [] : [problem(path, 'moet een punt [x, y, z] zijn')];
        case 'vector':
            return Array.isArray(value) &&
                value.length === 3 &&
                value.every((v) => isNumber(v) && v >= spec.min && v <= spec.max)
                ? []
                : [
                    problem(path, `moet [x, y, z] zijn, elk tussen ${dutch(spec.min)} en ${dutch(spec.max)}`),
                ];
        case 'names':
            return Array.isArray(value) &&
                value.length >= 1 &&
                value.length <= spec.max &&
                value.every((v) => typeof v === 'string' && MESH.test(v))
                ? []
                : [problem(path, `moet 1 tot ${spec.max} meshnamen zijn`)];
        case 'colours':
            return Array.isArray(value) &&
                value.length >= spec.min &&
                value.length <= spec.max &&
                value.every((v) => typeof v === 'string' && COLOUR.test(v))
                ? []
                : [problem(path, `moet ${spec.min} tot ${spec.max} kleuren als "#a1b2c3" zijn`)];
        case 'id':
            return typeof value === 'string' && ID.test(value)
                ? []
                : [problem(path, 'moet een naam zijn als "hal"')];
        case 'lamps':
            return lampsProblems(value, path);
        case 'rooms':
        case 'floors':
        case 'tables':
        case 'walls':
        case 'start':
        case 'photos':
            return placementProblems(spec.kind, value, path);
        case 'overlay': {
            if (value === null)
                return [];
            if (!isRecord(value) ||
                Object.keys(value).length !== 3 ||
                typeof value['platform'] !== 'string') {
                return [problem(path, 'moet null zijn of {platform: "<textuur>", tile, tint}')];
            }
            return [
                ...numberProblems({ min: 0.5, max: 100 }, value['tile'], `${path}/tile`),
                ...checkValue({ kind: 'colour', about: '' }, value['tint'], `${path}/tint`),
            ];
        }
    }
}
/** One block: refused whole for an unknown type; else every setting's problems, in the table's order. */
export function readBlock(raw, at, version = 1) {
    if (!isRecord(raw))
        return { problems: [problem(at, 'moet een blok {type, id, …} zijn')] };
    const type = raw['type'];
    const known = BLOCK_TYPES.filter((name) => settingsFor(name, version) !== undefined);
    const settings = typeof type === 'string' && BLOCK_TYPES.includes(type)
        ? settingsFor(type, version)
        : undefined;
    if (typeof type !== 'string' || !settings) {
        const later = typeof type === 'string' && BLOCK_TYPES.includes(type)
            ? `; "${type}" bestaat pas in formaatversie 2`
            : '';
        return {
            problems: [
                problem(`${at}/type`, `onbekend blok "${show(type)}" (wel: ${known.join(', ')}${later})`),
            ],
        };
    }
    const problems = [];
    if (typeof raw['id'] !== 'string' || !BLOCK_ID.test(raw['id'])) {
        problems.push(problem(`${at}/id`, 'moet een naam zijn als "zee" of "helm-2" (kleine letters, cijfers, -)'));
    }
    for (const [name, spec] of Object.entries(settings)) {
        if (!Object.hasOwn(raw, name) || raw[name] === undefined) {
            problems.push(problem(`${at}/${name}`, 'ontbreekt'));
        }
        else
            problems.push(...checkValue(spec, raw[name], `${at}/${name}`));
    }
    for (const name of Object.keys(raw)) {
        if (name !== 'type' && name !== 'id' && !Object.hasOwn(settings, name)) {
            problems.push(problem(`${at}/${show(name)}`, `onbekende instelling voor ${type}`));
        }
    }
    if (problems.length > 0)
        return { problems };
    // Rebuilt from the known keys only, copied: nothing of the caller's object is kept.
    const block = { type, id: raw['id'] };
    for (const name of Object.keys(settings))
        block[name] = JSON.parse(JSON.stringify(raw[name]));
    return { block: block, problems };
}
//# sourceMappingURL=settings.js.map